import React, { useContext, useEffect, useRef, useState } from "react";
import { Context } from "../../context/Context";
import "./Remote.css";
import EquipmentButtonIcons from "../../constants/EquipmentButtonIcon";
import {
  DeleteEquipmentButton,
  DeleteEquipments,
  UpdateEquipmentButton,
  UpdateEquipments,
} from "../../api/api";
import { Link, useNavigate, useParams } from "react-router-dom";
import SuccessToast from "../toasts/SuccessToast";
import { MqttContext } from "../../context/MQTTContext";
import WarningAlert from "../sweeAlerts/WarningAlert";
import "simplebar-react/dist/simplebar.min.css";

const InputStatus = () => {
  const {
    handleEquipmentButtonIconModal,
    setEditEquipmentButton,
    authUser,
    setIsLoading,
    equipmentButtons,
    setEquipmentButtons,
    devices,
    equipments,
    setEquipments,
    handleEquipmentsListModal,
    setSelectedEquipment,
    selectedEquipment,
    setChangeNameOptions,
    handleChangeNameModal,
    refreshMqtt,
    update,
    setUpdate,
    currentRoom,
  } = useContext(Context);
  const { clients } = useContext(MqttContext);
  const [buttons, setButtons] = useState([]);
  const [buttonStates, setButtonStates] = useState({});
  const [editButton, setEditButton] = useState(null);
  const [editEquipmentName, setEditEquipmentName] = useState(false);
  const [cleanupFunctions, setCleanupFunctions] = useState([]);

  const { equipment_id: eq_id, room_id, equipment_type: eq_type } = useParams();
  const navigate = useNavigate();

  const getDeviceForButton = (button) => {
    return devices.find((d) => d.id == button.device_id);
  };

  const getArrayIndex = (digit) => {
    if (digit >= 65 && digit <= 88) {
      return digit - 65;
    }
    else if (digit >= 89 && digit <= 112) {
      return digit - 65;
    }
    return digit;
  };

  const getButtonType = (digit) => {
    if (digit >= 65 && digit <= 88) return "input";
    if (digit >= 89 && digit <= 112) return "scenario";
    return "unknown";
  };

  const findButtonState = (button) => {
    const device = getDeviceForButton(button);
    if (!device || !buttonStates[device.id]) return null;
    
    const stateString = buttonStates[device.id];
    const arrayIndex = getArrayIndex(button.digit);
    
    if (arrayIndex >= 0 && arrayIndex < stateString.length) {
      const state = stateString[arrayIndex];
      return state === '0' ? 0 : state === '1' ? 1 : state === '2' ? 2 : null;
    }
    return null;
  };

  const calcButtonDigit = (button) => {
    return button.digit;
  };

  const renderSubName = (button) => {
    const device = getDeviceForButton(button);
    if (!device) return '';
    
    if (device.type == 3) {
      return 's' + (Number(button.digit) - 64);
    } else {
      if (button.digit >= 89) {
        return `s${Number(button.digit) - 88}`;
      } else {
        return `i${Number(button.digit) - 64}`;
      }
    }
  };

  const concatenateStringAndBuffer = (str, buffer) => {
    let encoder = new TextEncoder();
    let encodedString = encoder.encode(str);
    let newBuffer = new Uint8Array(encodedString.length + buffer.byteLength);
    newBuffer.set(encodedString, 0);
    newBuffer.set(new Uint8Array(buffer), encodedString.length);
    return newBuffer;
  };

  useEffect(() => {
    let btns = equipmentButtons
      .filter((eq_btn) => eq_btn.equipment_id == eq_id)
      .map((btn) => ({
        ...btn,
        digit: Number(btn.digit),
      }));

    setButtons(btns);
    setSelectedEquipment({
      id: eq_id,
      type: eq_type,
    });

    if (!btns.length) return;

    const cleanupFunctionsList = [];

    const uniqueDevices = [...new Set(btns.map(btn => btn.device_id))];
    
    uniqueDevices.forEach(deviceId => {
      let device = devices.find((d) => d.id == deviceId);
      if (!device) return;

      const setupMqttListener = () => {
        if (device.is_bus == 1 && clients[device?.bridge_device?.id]) {
          const messageListener = (topic, message) => {
            try {
              const endOfTwoCharToken =
                String.fromCharCode(message[0]) + String.fromCharCode(message[1]);
              const deviceSerial =
                String.fromCharCode(message[3]) +
                String.fromCharCode(message[4]) +
                String.fromCharCode(message[5]) +
                String.fromCharCode(message[6]) +
                String.fromCharCode(message[7]) +
                String.fromCharCode(message[8]);
              
              if (
                device.bridge_device.internet_token.slice(-2) === endOfTwoCharToken &&
                device.serial === deviceSerial &&
                message[10] == 16 &&
                message[2] == 14
              ) {
                let value =
                  `${message[11].toString(2).padStart(8, "0")}` +
                  `${message[12].toString(2).padStart(8, "0")}` +
                  `${message[13].toString(2).padStart(8, "0")}`;
                
                if (message[14]) {
                  value += `${message[14].toString(2).padStart(8, "0")}`;
                }
                if (message[15]) {
                  value += `${message[15].toString(2).padStart(8, "0")}`;
                }
                if (message[16]) {
                  value += `${message[16].toString(2).padStart(8, "0")}`;
                }
                
                const reversedState = value.split("").reverse().join("");
                
                setButtonStates((prev) => ({
                  ...prev,
                  [device.id]: reversedState,
                }));
              }
            } catch (error) {
              console.error("Error processing MQTT message:", error);
            }
          };

          clients[device.bridge_device.id].on("message", messageListener);
          
          cleanupFunctionsList.push(() => {
            if (clients[device.bridge_device.id]) {
              clients[device.bridge_device.id].removeListener("message", messageListener);
            }
          });

          let buffer = new ArrayBuffer(11);
          let asciiCode = new Uint8Array(buffer);
          asciiCode[0] = 14;
          asciiCode[1] = `${device.serial[0]}`.charCodeAt(0);
          asciiCode[2] = `${device.serial[1]}`.charCodeAt(0);
          asciiCode[3] = `${device.serial[2]}`.charCodeAt(0);
          asciiCode[4] = `${device.serial[3]}`.charCodeAt(0);
          asciiCode[5] = `${device.serial[4]}`.charCodeAt(0);
          asciiCode[6] = `${device.serial[5]}`.charCodeAt(0);
          asciiCode[7] = device.type + 50;
          asciiCode[8] = 1;
          asciiCode[9] = 64;
          asciiCode[10] = 64;

          let message = device.bridge_device.internet_token;
          let combinedMessage = concatenateStringAndBuffer(message, asciiCode);
          
          if (clients[device.bridge_device.id]) {
            clients[device.bridge_device.id].publish(
              device.bridge_device.serial + "/sys",
              combinedMessage,
            );
          }
        } 
        else if (
          device.id &&
          device.username &&
          device.password &&
          clients[device.id]
        ) {
          const messageListener = (topic, message) => {
            try {
              const endOfTwoCharToken =
                String.fromCharCode(message[0]) + String.fromCharCode(message[1]);
              
              if (
                device.internet_token.slice(-2) === endOfTwoCharToken &&
                message[2] == 16
              ) {
                let value =
                  `${message[3].toString(2).padStart(8, "0")}` +
                  `${message[4].toString(2).padStart(8, "0")}` +
                  `${message[5].toString(2).padStart(8, "0")}`;
                
                if (message[6]) {
                  value += `${message[6].toString(2).padStart(8, "0")}`;
                }
                if (message[7]) {
                  value += `${message[7].toString(2).padStart(8, "0")}`;
                }
                if (message[8]) {
                  value += `${message[8].toString(2).padStart(8, "0")}`;
                }
                
                const reversedState = value.split("").reverse().join("");
                
                setButtonStates((prev) => ({
                  ...prev,
                  [device.id]: reversedState,
                }));
              }
            } catch (error) {
              console.error("Error processing MQTT message:", error);
            }
          };
          
          clients[device.id].on("message", messageListener);
          
          cleanupFunctionsList.push(() => {
            if (clients[device.id]) {
              clients[device.id].removeListener("message", messageListener);
            }
          });

          let buffer = new ArrayBuffer(3);
          let asciiCode = new Uint8Array(buffer);
          asciiCode[0] = 1;
          asciiCode[1] = 64;
          asciiCode[2] = 64;

          let message = device.internet_token;
          let combinedMessage = concatenateStringAndBuffer(message, asciiCode);
          
          if (clients[device.id]) {
            clients[device.id].publish(device.serial + "/sys", combinedMessage);
          }
        }
      };

      setupMqttListener();
    });

    setCleanupFunctions(cleanupFunctionsList);

    return () => {
      cleanupFunctionsList.forEach(cleanup => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
    };
  }, [update, equipmentButtons, refreshMqtt, eq_id, devices, clients]);

  const handleClickButton = (button) => {
    let device = getDeviceForButton(button);
    if (!device) return;

    const currentState = buttonStates[device.id];
    if (!currentState) return;

    const digit = calcButtonDigit(button);
    const currentButtonState = findButtonState(button);
    
    if (currentButtonState === 2) return;

    const arrayIndex = getArrayIndex(digit);
    
    let stateArray = currentState.split("");
    
    if (arrayIndex >= 0 && arrayIndex < stateArray.length) {
      stateArray[arrayIndex] = "2";
      const newState = stateArray.join("");
      
      setButtonStates((prevStates) => ({
        ...prevStates,
        [device.id]: newState,
      }));
    }

    const newStatus = currentButtonState === 1 ? 0 : 1;
    
    if (device.is_bus == 1 && clients[device?.bridge_device?.id]) {
      let buffer = new ArrayBuffer(11);
      let asciiCode = new Uint8Array(buffer);
      asciiCode[0] = 14;
      asciiCode[1] = `${device.serial[0]}`.charCodeAt(0);
      asciiCode[2] = `${device.serial[1]}`.charCodeAt(0);
      asciiCode[3] = `${device.serial[2]}`.charCodeAt(0);
      asciiCode[4] = `${device.serial[3]}`.charCodeAt(0);
      asciiCode[5] = `${device.serial[4]}`.charCodeAt(0);
      asciiCode[6] = `${device.serial[5]}`.charCodeAt(0);
      asciiCode[7] = device.type + 50;
      asciiCode[8] = 1;
      asciiCode[9] = digit;
      asciiCode[10] = newStatus;

      let message = device.bridge_device.internet_token;
      let combinedMessage = concatenateStringAndBuffer(message, asciiCode);
      clients[device.bridge_device.id].publish(
        device.bridge_device.serial + "/sys",
        combinedMessage,
      );
    } 
    else if (clients[device.id]) {
      let buffer = new ArrayBuffer(3);
      let asciiCode = new Uint8Array(buffer);
      asciiCode[0] = 1;
      asciiCode[1] = digit;
      asciiCode[2] = newStatus;

      let message = device.internet_token;
      let combinedMessage = concatenateStringAndBuffer(message, asciiCode);
      clients[device.id].publish(device.serial + "/sys", combinedMessage);
    }
  };

  const handleRemoveButton = (e, button) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    
    DeleteEquipmentButton({
      equipment_button_id: button.id,
    }).then((res) => {
      if (res.status == 200) {
        setEquipmentButtons(equipmentButtons.filter((b) => b.id != button.id));
        SuccessToast("با موفقیت حذف شد");
        setIsLoading(false);
      }
    }).catch((error) => {
      console.error("Error removing button:", error);
      setIsLoading(false);
    });
  };

  const handleChangeNameButton = (button) => {
    const onSubmitFunc = ({ button, name }) => {
      UpdateEquipmentButton({
        equipment_button_id: button.id,
        name: name,
        digit: button.digit,
        icon: button.icon,
      }).then((res) => {
        if (res.status == 200) {
          setEditButton(null);
          SuccessToast("با موفقیت ثبت شد");

          const index = equipmentButtons.findIndex(
            (item) => item.id === button.id,
          );

          if (index !== -1) {
            const updatedButtons = [...equipmentButtons];
            updatedButtons[index] = {
              ...updatedButtons[index],
              name: name,
            };
            setEquipmentButtons(updatedButtons);
          }
        }
      }).catch((error) => {
        console.error("Error updating button name:", error);
      });
    };
    
    const newOptions = {
      name: button.name,
      onSubmit: (newName) => {
        onSubmitFunc({ button, name: newName });
      },
    };
    setChangeNameOptions(newOptions);
    handleChangeNameModal();
  };

  const handleChangeEquipmentName = (equipment_id) => {
    const onSubmitFunc = ({ equipment_id, name }) => {
      UpdateEquipments({
        equipment_id,
        name,
      }).then((res) => {
        if (res.status == 200) {
          const updatedEquipments = equipments.map((item) =>
            item.id === equipment_id
              ? {
                  ...item,
                  equipment: { ...item.equipment, name: name },
                }
              : item,
          );
          setEquipments(updatedEquipments);
          SuccessToast("با موفقیت ثبت شد");
        }
      }).catch((error) => {
        console.error("Error updating equipment name:", error);
      });
    };
    
    const newOptions = {
      name: equipments.find((eq) => eq.id == equipment_id)?.equipment?.name ?? "",
      onSubmit: (newName) => {
        onSubmitFunc({ equipment_id, name: newName });
      },
    };
    setChangeNameOptions(newOptions);
    handleChangeNameModal();
  };

  const handleRemoveEquipment = (equipment_id) => {
    WarningAlert(
      "آیا از حذف این تجهیز اطمینان دارید",
      "",
      "بله",
      "انصراف",
    ).then(function (result) {
      if (result.value) {
        setIsLoading(true);
        DeleteEquipments({ equipment_id }).then((res) => {
          SuccessToast(res.data.message);
          let newEquipments = equipments.filter((eq) => eq.id != equipment_id);
          setEquipments(newEquipments);
          let lstEquipment = newEquipments.find(
            (eq) => eq.room_id == currentRoom,
          );
          handleEquipment(lstEquipment, currentRoom);
          setIsLoading(false);
        }).catch((error) => {
          console.error("Error removing equipment:", error);
          setIsLoading(false);
        });
      }
    });
  };

  const handleEquipment = (equipment, roomId) => {
    if (!equipment) return navigate("/room/" + roomId);
    equipment = equipment.equipment;
    setSelectedEquipment({
      id: equipment.id,
      type: equipment.type,
    });
    
    const routes = {
      1: `/room/key/${roomId}/${equipment.id}/${equipment.type}`,
      3: `/room/remote/${roomId}/${equipment.id}/${equipment.type}`,
      4: `/room/dimmer/${roomId}/${equipment.id}/${equipment.type}`,
      5: `/room/thermostat/${roomId}/${equipment.id}/${equipment.type}`,
      9: `/room/rgb/${roomId}/${equipment.id}/${equipment.type}`,
      99: `/room/wireless-key/${roomId}/${equipment.id}/${equipment.type}`,
      100: `/room/television/${roomId}/${equipment.id}/${equipment.type}`,
      401: `/room/scenario-key/${roomId}/${equipment.id}/${equipment.type}`,
      501: `/room/fan-coil/${roomId}/${equipment.id}/${equipment.type}`,
      502: `/room/motor/${roomId}/${equipment.id}/${equipment.type}`,
      503: `/room/humidity/${roomId}/${equipment.id}/${equipment.type}`,
      600: `/room/data-logger/${roomId}/${equipment.id}/${equipment.type}`,
      9998: `/room/input/${roomId}/${equipment.id}/${equipment.type}`,
      9999: `/room/input-status/${roomId}/${equipment.id}/${equipment.type}`,
    };
    
    const route = routes[equipment.type];
    if (route) {
      navigate(route);
    } else {
      navigate("/");
    }
  };

  const renderButtons = () => {
    return buttons.map((button) => {
      let device = getDeviceForButton(button);
      if (!device) return null;
      
      let iconPath = "/assets/img/custom-equipment-button/19.png";
      if (button.icon) {
        let buttonIcon = EquipmentButtonIcons.find(
          (icon) => icon.id == button.icon,
        );
        if (buttonIcon) {
          iconPath = buttonIcon.path;
        }
      }

      let buttonState = findButtonState(button);
      const deviceSerial = device.serial || '';
      const buttonType = getButtonType(button.digit);

      return (
        <div
          className="remote"
          key={button.id}
          onClick={() => handleClickButton(button)}
          style={{ cursor: 'pointer' }}
        >
          {authUser.is_admin ? (
            <div
              className="btn-group d-inline-block"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <button
                type="button"
                className="btn btn-icon rounded-pill dropdown-toggle hide-arrow"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bx bx-dots-vertical-rounded"></i>
              </button>
              <ul className="dropdown-menu">
                <li
                  onClick={() => {
                    handleEquipmentButtonIconModal(true);
                    setEditEquipmentButton(button);
                  }}
                >
                  <a className="dropdown-item" href="#">تغییر آیکون</a>
                </li>
                <li
                  onClick={() => {
                    handleChangeNameButton(button);
                  }}
                >
                  <a className="dropdown-item" href="#">تغییر نام</a>
                </li>
                <li onClick={(e) => handleRemoveButton(e, button)}>
                  <a className="dropdown-item" href="#">حذف</a>
                </li>
              </ul>
            </div>
          ) : null}

          <div className="icon-part">
            <img src={iconPath} alt={button.name} />
          </div>
          <div
            className={
              buttonState == 1
                ? "status-part bg-success"
                : buttonState == 0
                ? "status-part bg-danger"
                : buttonState == 2
                ? "status-part bg-info"
                : "status-part"
            }
          ></div>
          <div className="info-part">
            <p className="text-center p-0 m-0 d-block">
              {button.name || `Channel ${button.digit}`}
            </p>
            <p className="text-center p-0 m-0" style={{ fontSize: "11px" }}>
              {renderSubName(button)} - S/{deviceSerial}
            </p>
          </div>
        </div>
      );
    });
  };

  const currentEquipment = equipments.find((eq) => eq.id == eq_id);

  return (
    <div
      className="card h-100"
      style={{ backgroundColor: "#fff0", boxShadow: "none" }}
    >
      <div
        className="card-header d-flex align-items-center justify-content-between border-bottom mb-3 p-0 pt-2"
        style={{ zIndex: "1" }}
      >
        <h5 className={editEquipmentName ? "d-none" : "m-0"}>
          {currentEquipment?.equipment?.name ?? "No Name"}
        </h5>
        <div>
          <button
            type="button"
            className="btn btn-icon btn-success-app mx-3"
            onClick={() => {
              setUpdate((prev) => !prev);
            }}
            title="تازه سازی"
          >
            <span className="tf-icons bx bx-refresh"></span>
          </button>
          {authUser.is_admin ? (
            <>
              <button
                type="button"
                className="btn btn-success-app btn-icon me-2"
                style={{ borderColor: "rgb(0, 165, 148)" }}
                onClick={() => handleRemoveEquipment(selectedEquipment.id)}
                title="حذف تجهیز"
              >
                <i className="bx bx-trash"></i>
              </button>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-success-app btn-icon dropdown-toggle hide-arrow"
                  style={{ borderColor: "rgb(0, 165, 148)" }}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bx bx-dots-vertical-rounded"></i>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <Link
                      className="dropdown-item"
                      onClick={(e) => {
                        e.preventDefault();
                        handleEquipmentsListModal(true);
                      }}
                      to="#"
                    >
                      افزودن تجهیز
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="dropdown-item"
                      to={
                        "/room/select-device/" +
                        room_id +
                        "/" +
                        eq_id +
                        "/" +
                        eq_type
                      }
                    >
                      افزودن کانال به تجهیز
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="dropdown-item"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentEquipment) {
                          handleChangeEquipmentName(currentEquipment.id);
                        }
                      }}
                      to="#"
                    >
                      تغییر نام تجهیز
                    </Link>
                  </li>
                </ul>
              </div>
            </>
          ) : null}
        </div>
      </div>
      <div
        className="card-body scrollbar mt-4"
        style={{ paddingBottom: "10rem" }}
      >
        <div className="d-flex flex-wrap justify-content-between justify-content-sm-start">
          {renderButtons()}
        </div>
      </div>
    </div>
  );
};

export default React.memo(InputStatus);